/**
 * 警報聲音服務 — Web Audio API 合成提示音
 *
 * 依嚴重度播放不同頻率/節奏的 beep 音效：
 * - CRITICAL: 880Hz 三連急促 beep
 * - ALARM:    660Hz 雙響 beep
 * - WARNING:  520Hz 單響 beep
 * - INFO:     不播放
 */
import type { AlarmSeverity } from '../types/alarm';

const STORAGE_KEY_MUTED = 'alarm_sound_muted';

/** 嚴重度優先順序（數字越小越高） */
const SEVERITY_PRIORITY: Record<string, number> = {
  CRITICAL: 0,
  ALARM: 1,
  WARNING: 2,
  INFO: 3,
};

/** 各嚴重度的音效參數 */
interface TonePattern {
  frequency: number;
  /** 每次 beep 持續時間（秒） */
  duration: number;
  /** beep 之間的間隔（秒） */
  gap: number;
  /** 重複次數 */
  count: number;
}

const TONE_PATTERNS: Record<string, TonePattern | null> = {
  CRITICAL: { frequency: 880, duration: 0.1, gap: 0.08, count: 3 },
  ALARM: { frequency: 660, duration: 0.15, gap: 0.12, count: 2 },
  WARNING: { frequency: 520, duration: 0.2, gap: 0, count: 1 },
  INFO: null, // 不播放
};

class AlarmSoundService {
  private ctx: AudioContext | null = null;
  private _muted: boolean;
  private volume = 0.5;
  private lastPlayTime = 0;
  private playing = false;
  private static readonly MIN_INTERVAL = 2000; // 最小播放間隔（毫秒）

  constructor() {
    // 預設靜音（未啟用），dev 手動開啟後才會存 'false' 到 localStorage
    const stored = localStorage.getItem(STORAGE_KEY_MUTED);
    this._muted = stored === null ? true : stored === 'true';
  }

  /** 取得或建立 AudioContext（需使用者互動後才能 resume） */
  private ensureContext(): AudioContext {
    if (!this.ctx || this.ctx.state === 'closed') {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume().catch((e) => console.warn('[AlarmSound] Failed to resume audio context:', e));
    }
    return this.ctx;
  }

  /** 播放單次 beep */
  private playBeep(ctx: AudioContext, frequency: number, startTime: number, duration: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.type = 'square';
    osc.frequency.value = frequency;

    // 淡入淡出避免爆音
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(this.volume, startTime + 0.01);
    gain.gain.setValueAtTime(this.volume, startTime + duration - 0.01);
    gain.gain.linearRampToValueAtTime(0, startTime + duration);

    osc.connect(gain);
    gain.connect(ctx.destination);

    osc.start(startTime);
    osc.stop(startTime + duration);
  }

  /** 播放指定嚴重度的警報音效 */
  play(severity: AlarmSeverity): void {
    if (this._muted) return;

    const pattern = TONE_PATTERNS[severity];
    if (!pattern) return; // INFO 不播放

    // 防重疊 + 最小間隔
    const now = Date.now();
    if (this.playing || now - this.lastPlayTime < AlarmSoundService.MIN_INTERVAL) return;

    this.playing = true;
    this.lastPlayTime = now;

    try {
      const ctx = this.ensureContext();
      const startTime = ctx.currentTime + 0.05; // 微小延遲確保穩定

      for (let i = 0; i < pattern.count; i++) {
        const offset = i * (pattern.duration + pattern.gap);
        this.playBeep(ctx, pattern.frequency, startTime + offset, pattern.duration);
      }

      // 計算總時長，播放結束後重置 playing 標誌
      const totalDuration = pattern.count * pattern.duration + (pattern.count - 1) * pattern.gap;
      setTimeout(() => { this.playing = false; }, totalDuration * 1000 + 100);
    } catch (e) {
      console.warn('[AlarmSound] Failed to play alarm:', e);
      this.playing = false;
    }
  }

  /** 設定靜音狀態 */
  setMuted(muted: boolean): void {
    this._muted = muted;
    try {
      localStorage.setItem(STORAGE_KEY_MUTED, String(muted));
    } catch (e) { console.warn('[AlarmSound] Failed to save muted state:', e); }
  }

  /** 切換靜音 */
  toggleMute(): boolean {
    const next = !this._muted;
    this.setMuted(next);
    return next;
  }

  isMuted(): boolean {
    return this._muted;
  }
}

/** 從一組警報中取得最高嚴重度 */
export function getHighestSeverity(alarms: { severity: AlarmSeverity }[]): AlarmSeverity {
  let best: AlarmSeverity = 'INFO';
  let bestPri = SEVERITY_PRIORITY.INFO;
  for (const a of alarms) {
    const pri = SEVERITY_PRIORITY[a.severity] ?? 3;
    if (pri < bestPri) {
      bestPri = pri;
      best = a.severity;
    }
  }
  return best;
}

export default new AlarmSoundService();
